import { FormattedMessage } from "react-intl";
import ColorLink from "../components/common/links/colorLink";
import { CameraIcon } from "@heroicons/react/24/outline";
import AvatarWithText from "../components/common/avatars/avatarWithText";
import { Link } from "@inertiajs/react";
import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import AppLayout from "../layouts/appLayout";
import { Article } from "@/types/article";
import { User } from "@/types/user";
import { Collaborator } from "@/types/collaborator";
import { Account } from "@/types/account";

interface ArticleDetailInterface {
    article: Article & {
        user: User & { name: string; initials: string; image: string };
        collaborator: Collaborator;
        createdAt: string;
        publishedAt: string;
        account: Account;
    };
    content: string;
    title: string;
    image: string;
    description: string;
}

export default function ArticleDetail({
    article,
    content,
    title,
    image,
    description,
}: ArticleDetailInterface) {
    return (
        <AppLayout title={title} image={image} description={description}>
            <div>
                <div className="sm:hidden flex flex-col space-y-4">
                    <div className="mx-6">
                        <ColorLink
                            color="white"
                            size="sm"
                            href={route("news-index")}
                        >
                            <FormattedMessage id="Back" />
                        </ColorLink>
                    </div>
                    {image ? (
                        <div className="aspect-w-10 aspect-h-7 overflow-hidden">
                            <img className="object-cover" src={image} alt="" />
                        </div>
                    ) : (
                        <div className="aspect-w-10 aspect-h-7 bg-rose-100 text-red-400">
                            <div className="flex items-center justify-center">
                                <CameraIcon className="h-32 w-32" />
                            </div>
                        </div>
                    )}
                    <div className="flex flex-col justify-center mx-6">
                        {article.publishedAt ? (
                            <p className="mt-8 font-medium text-red-600 text-sm">
                                {article.publishedAt}
                            </p>
                        ) : (
                            <p className="mt-8 font-medium text-gray-600 text-sm">
                                {article.createdAt}
                            </p>
                        )}
                        <p className="mt-2 text-2xl font-bold tracking-tight text-gray-900">
                            {article.title}
                        </p>
                        <AvatarWithText
                            user={article.user}
                            subtitle={article?.account?.name}
                            className="mt-4"
                        />
                    </div>
                </div>
                <div className="hidden sm:max-w-[1600px] sm:mx-auto sm:px-6 sm:flex sm:h-136">
                    <div className="w-1/2 flex flex-col justify-center pr-12">
                        <div className="flex-none flex flex-col justify-center h-14">
                            <Link
                                href={route("news-index")}
                                className="flex items-center text-gray-700"
                            >
                                <ChevronLeftIcon className="size-4" />
                                <span className="underline underline-offset-4">
                                    <FormattedMessage id="Back" />
                                </span>
                            </Link>
                        </div>
                        <div className="grow flex flex-col justify-center">
                            {article.publishedAt ? (
                                <p className="-mt-14 font-medium text-red-600 text-sm">
                                    {article.publishedAt}
                                </p>
                            ) : (
                                <p className="-mt-14 font-medium text-gray-600 text-sm">
                                    {article.createdAt}
                                </p>
                            )}
                            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                                {article.title}
                            </p>
                            <AvatarWithText
                                user={article.user}
                                subtitle={article?.account?.name}
                                className="mt-4"
                            />
                        </div>
                    </div>
                    <div className="w-1/2 flex flex-col justify-center items-center bg-gray-100">
                        <div className="w-2/3 aspect-w-10 aspect-h-7 overflow-hidden">
                            {image ? (
                                <img
                                    className="object-cover"
                                    src={image}
                                    alt=""
                                />
                            ) : (
                                <div className="w-full h-full bg-rose-100 text-red-400 flex items-center justify-center">
                                    <CameraIcon className="size-48" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="py-12 px-6 sm:py-24">
                    <div
                        className="prose mx-auto"
                        dangerouslySetInnerHTML={{ __html: content }}
                    />
                </div>
            </div>
        </AppLayout>
    );
}
